if (!window.customElements.get("cart-drawer-recommendations")) {
  window.customElements.define(
    "cart-drawer-recommendations",
    class CartDrawerRecommendations extends HTMLElement {
      connectedCallback() {
        this.abortController = new AbortController();
        this.requestId = 0;
        this.preservedDesktop = null;
        this.preservedMobile = null;

        document.addEventListener("cart:change", this.onCartChange, {
          capture: true,
          signal: this.abortController.signal
        });
        document.addEventListener("cart:refresh", this.preserveTargets, {
          capture: true,
          signal: this.abortController.signal
        });
        document.addEventListener("cart-drawer:refreshed", this.onDrawerRefreshed, {
          signal: this.abortController.signal
        });

        this.log("connected", {
          productId: this.productId,
          sectionId: this.sectionId,
          intent: this.intent
        });

        this.load();
      }

      disconnectedCallback() {
        this.abortController.abort();
      }

      get drawer() {
        return document.getElementById("cart-drawer");
      }

      get sectionId() {
        return this.getAttribute("section-id");
      }

      get productId() {
        return this.getAttribute("product-id");
      }

      get limit() {
        return this.getAttribute("limit") || "10";
      }

      get intent() {
        return this.getAttribute("intent") || "related";
      }

      getDesktopTarget() {
        return this.drawer?.querySelector("[data-cart-recommendations-desktop]");
      }

      getMobileTarget() {
        return this.drawer?.querySelector("[data-cart-recommendations-mobile]");
      }

      preserveTargets = () => {
        this.preservedDesktop = this.getDesktopTarget();
        this.preservedMobile = this.getMobileTarget();
      };

      onCartChange = (event) => {
        const items = event.detail?.cart?.items || [];

        if (items.length === 0) {
          this.requestId += 1;
          this.preservedDesktop = null;
          this.preservedMobile = null;
          this.setAttribute("product-id", "");
          this.clear(this.getDesktopTarget());
          this.clear(this.getMobileTarget());
          return;
        }

        this.log("cart:change", {
          itemCount: items.length,
          productId: items[0]?.product_id || ""
        });
        this.preserveTargets();
        this.setAttribute("product-id", items[0]?.product_id ? String(items[0].product_id) : "");
      };

      onDrawerRefreshed = () => {
        this.restorePreservedTargets();
        this.load({ showLoader: true });
      };

      restorePreservedTargets() {
        const desktop = this.getDesktopTarget();
        const mobile = this.getMobileTarget();

        if (this.preservedDesktop && desktop && this.preservedDesktop !== desktop) {
          desktop.replaceWith(this.preservedDesktop);
        }

        if (this.preservedMobile && mobile && this.preservedMobile !== mobile) {
          mobile.replaceWith(this.preservedMobile);
        }

        this.preservedDesktop = null;
        this.preservedMobile = null;
      }

      log(...args) {
        console.log("[cart-recs]", ...args);
      }

      async load({ showLoader = false } = {}) {
        const desktop = this.getDesktopTarget();
        const mobile = this.getMobileTarget();

        this.log("load()", {
          productId: this.productId,
          sectionId: this.sectionId,
          limit: this.limit,
          intent: this.intent,
          showLoader,
          desktopTarget: Boolean(desktop),
          mobileTarget: Boolean(mobile)
        });

        if (!this.productId || !this.sectionId) {
          this.log("skip fetch: missing productId or sectionId");
          this.requestId += 1;
          this.clear(desktop);
          this.clear(mobile);
          return;
        }

        const requestId = ++this.requestId;

        this.ensureSkeletons(desktop, "desktop");
        this.ensureSkeletons(mobile, "mobile");

        if (showLoader) {
          this.setBusy(desktop, true);
          this.setBusy(mobile, true);
        }

        const root = window.Shopify.routes.root;
        const jsonUrl = `${root}recommendations/products.json?product_id=${this.productId}&limit=${this.limit}&intent=${this.intent}`;
        const htmlUrl = `${root}recommendations/products?product_id=${this.productId}&limit=${this.limit}&section_id=${this.sectionId}&intent=${this.intent}`;

        try {
          const jsonResponse = await fetch(jsonUrl);
          if (requestId !== this.requestId) {
            this.log("skip inject after JSON: stale request", { requestId, currentRequestId: this.requestId });
            return;
          }
          const jsonData = jsonResponse.ok ? await jsonResponse.json() : await jsonResponse.text();
          const jsonProducts = jsonData?.products || [];

          this.log("JSON request", {
            url: jsonUrl,
            status: jsonResponse.status,
            ok: jsonResponse.ok,
            productsCount: jsonProducts.length,
            products: jsonProducts.map((product) => ({
              id: product.id,
              title: product.title,
              handle: product.handle,
              available: product.available
            })),
            raw: jsonData
          });

          const response = await fetch(htmlUrl);
          const html = await response.text();

          this.log("HTML request", {
            url: htmlUrl,
            status: response.status,
            ok: response.ok,
            htmlLength: html.length
          });

          if (!response.ok || requestId !== this.requestId) {
            this.log("skip inject", { ok: response.ok, requestId, currentRequestId: this.requestId });
            return;
          }

          const doc = new DOMParser().parseFromString(html, "text/html");
          const source = doc.querySelector("[data-cart-recommendations-source]");
          const content = source?.content || source;
          const debug = content?.querySelector("[data-cart-recommendations-debug]");
          const desktopSource = content?.querySelector('[data-layout="desktop"]');
          const mobileSource = content?.querySelector('[data-layout="mobile"]');

          this.log("parsed HTML", {
            templateFound: Boolean(source),
            templateIsTemplate: source?.tagName === "TEMPLATE",
            debug: debug
              ? {
                  performed: debug.getAttribute("data-performed"),
                  intent: debug.getAttribute("data-intent"),
                  count: debug.getAttribute("data-count")
                }
              : null,
            desktopCards: desktopSource?.childElementCount || 0,
            mobileCards: mobileSource?.childElementCount || 0,
            templateHTML: (source?.innerHTML || content?.innerHTML || "").slice(0, 500)
          });

          this.inject(this.getDesktopTarget(), desktopSource);
          this.inject(this.getMobileTarget(), mobileSource, true);
        } catch (error) {
          this.log("fetch error", error);
          if (requestId === this.requestId) {
            this.clear(this.getDesktopTarget());
            this.clear(this.getMobileTarget());
          }
        }
      }

      ensureSkeletons(target, type) {
        if (!target || target.querySelector(".product-card, .cart-recommendation-card")) {
          return;
        }

        const skeleton = '<div class="cart-drawer-recommendations__skeleton"></div>';
        const skeletons = `${skeleton}${skeleton}${skeleton}`;

        target.hidden = false;
        target.setAttribute("aria-busy", "true");
        target.innerHTML =
          type === "mobile"
            ? `<div class="cart-drawer-recommendations__scroller">${skeletons}</div>`
            : skeletons;
      }

      setBusy(target, isBusy) {
        if (!target) {
          return;
        }

        if (isBusy) {
          target.hidden = false;
          target.setAttribute("aria-busy", "true");
        } else {
          target.removeAttribute("aria-busy");
        }
      }

      inject(target, source, wrapCarousel = false) {
        this.log("inject()", {
          hasTarget: Boolean(target),
          hasSource: Boolean(source),
          sourceChildren: source?.childElementCount || 0,
          wrapCarousel
        });

        if (!target) {
          return;
        }

        if (!source || source.childElementCount === 0) {
          this.log("inject() cleared target: no source or empty source");
          this.clear(target);
          return;
        }

        const content = source.cloneNode(true);

        if (wrapCarousel) {
          const scroller = document.createElement("div");
          scroller.className = "cart-drawer-recommendations__scroller scroll-area snap-x";
          scroller.append(...content.childNodes);
          target.replaceChildren(scroller);
        } else {
          target.replaceChildren(...content.childNodes);
        }

        target.hidden = false;
        target.removeAttribute("aria-busy");
        this.log("inject() done", {
          target: target.className,
          children: target.childElementCount
        });
      }

      clear(target) {
        if (!target) {
          return;
        }

        target.replaceChildren();
        target.hidden = true;
        target.removeAttribute("aria-busy");
      }
    }
  );
}
